import { userListKey } from '@/hooks/keyUtils'
import { useAuth0 } from '@auth0/auth0-react'
import { UserListResult, getUserList } from '@infraloop/services'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { SortState } from '../utils'

interface UseUserListParams {
  /** Committed Auth0 email search (empty for no filter). */
  email: string
  /** Sort state of the subscription-end column (`undefined` leaves the list unsorted). */
  sortDir: SortState
  /** Page size — number of users per keyset page. */
  limit: number
  /** Keyset cursor for the requested page (undefined for the first page). */
  lastEvaluatedKey?: Record<string, string>
  /** Only fetch when the viewer is an admin (the endpoint is admin-only). */
  enabled?: boolean
}

/**
 * Loads one keyset page of the admin user list (always all users, not HubSpot-only).
 * Search/sort/cursor are query inputs, so each combination caches independently;
 * `keepPreviousData` keeps the current page visible while the next one loads.
 * An undefined `sortDir` omits the sort parameter, so the API returns its natural order.
 */
const useUserList = ({ email, sortDir, limit, lastEvaluatedKey, enabled = true }: UseUserListParams) => {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery<UserListResult>({
    queryKey: userListKey({ email, sortDir, limit, lastEvaluatedKey }),
    enabled,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return getUserList(accessToken, {
        hubspotOnly: false,
        email: email || undefined,
        baseFeatureEndAt: sortDir,
        limit,
        lastEvaluatedKey,
      })
    },
  })

  return {
    users: query.data?.results ?? [],
    lastEvaluatedKey: query.data?.last_evaluated_key,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  }
}

export default useUserList
